import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadProtectedFile } from "@/lib/fileAccess";
import {
  officialEmail,
  personalEmail,
  communicationAddress,
  permanentAddress,
} from "@/lib/employeeContact";
import { resolveWorkState, workModeLabel, workPlaceLabel } from "@/lib/workPlace";
import { formatDisplayDate } from "@/lib/dateFormat";
import { X, Mail, Phone, MapPin, Briefcase, Calendar, CreditCard, FileText, User, Laptop } from "lucide-react";

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
    <div className="col-span-2 text-sm text-slate-800">{value || <span className="text-slate-400">—</span>}</div>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="border border-slate-200 rounded">
    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#226DB4]" />
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export default function EmployeeView({ emp, department, designation, onClose }) {
  const commAddr = useMemo(() => communicationAddress(emp?.contact), [emp]);
  const permAddr = useMemo(() => permanentAddress(emp?.contact), [emp]);

  const [assets, setAssets] = useState([]);
  const [locMap, setLocMap] = useState({});
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (!emp?.id) {
      setAssets([]);
      return;
    }
    api
      .get(`/employees/${emp.id}/assets`)
      .then((r) => setAssets(r.data || []))
      .catch(() => setAssets([]));
  }, [emp?.id]);

  useEffect(() => {
    Promise.all([
      api.get("/locations").catch(() => ({ data: [] })),
      api
        .get("/company", { params: { company_id: emp?.company_id || "company" } })
        .catch(() => ({ data: null })),
    ]).then(([locRes, companyRes]) => {
      setLocMap(Object.fromEntries((locRes.data || []).map((l) => [l.id, l])));
      setCompany(companyRes.data);
    });
  }, [emp?.company_id]);

  if (!emp) return null;
  const initials = `${emp.basic?.first_name?.[0] || ""}${
    emp.basic?.last_name?.[0] || ""
  }`.toUpperCase();
  const e = emp.employment || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-[#f9fafb]">
          <div className="flex items-center gap-4">
            {emp.basic?.photo_url ? (
              <img
                src={emp.basic.photo_url}
                alt=""
                className="h-14 w-14 rounded-full object-cover border border-slate-200"
              />
            ) : (
              <div className="h-14 w-14 rounded-full bg-[#226DB4] text-white flex items-center justify-center font-semibold text-lg">
                {initials}
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {emp.basic.first_name} {emp.basic.last_name}
              </h2>
              <p className="text-sm text-slate-500">
                {designation?.title || "—"} · {department?.name || "—"} · {emp.basic.code}
              </p>
            </div>
          </div>
          <button
            data-testid="view-close-btn"
            onClick={onClose}
            className="p-2 text-slate-400 hover:bg-slate-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <Section title="Basic Info" icon={User}>
            <Row label="Code" value={emp.basic.code} />
            <Row label="Gender" value={emp.basic.gender} />
            <Row label="Date of Birth" value={formatDisplayDate(emp.basic.date_of_birth)} />
            <Row label="Blood Group" value={emp.basic.blood_group} />
            <Row label="Marital Status" value={emp.basic.marital_status} />
          </Section>

          <Section title="Contact" icon={Mail}>
            <Row label="Official Email" value={officialEmail(emp.contact)} />
            <Row label="Personal Email" value={personalEmail(emp.contact)} />
            <Row label="Phone" value={emp.contact.phone} />
            <Row label="Alt Phone" value={emp.contact.alt_phone} />
            <Row label="Communication Address" value={commAddr} />
            <Row label="Permanent Address" value={permAddr} />
            <Row label="Emergency Contact" value={emp.contact.emergency_contact_name && `${emp.contact.emergency_contact_name} (${emp.contact.emergency_contact_phone || "—"})`} />
          </Section>

          <Section title={`Education (${emp.education?.length || 0})`} icon={User}>
            {(!emp.education || emp.education.length === 0) ? (
              <div className="text-sm text-slate-400">No education records.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {emp.education.map((ed, i) => (
                  <li key={ed.id || i} className="py-2 text-sm">
                    <div className="font-medium text-slate-700">{ed.qualification}</div>
                    <div className="text-xs text-slate-500">
                      {[ed.institution, ed.board_university, ed.year, ed.grade, ed.specialization]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Employment" icon={Briefcase}>
            <Row label="Department" value={department?.name} />
            <Row label="Designation" value={designation?.title} />
            <Row label="Type" value={e.employment_type} />
            <Row label="Status" value={e.status} />
            <Row label="Joining Date" value={formatDisplayDate(e.joining_date)} />
            <Row label="Work Place" value={workModeLabel(e.work_mode)} />
            <Row label="Place / Branch" value={workPlaceLabel(emp, locMap)} />
            <Row label="State (PT)" value={resolveWorkState(emp, locMap, company)} />
            <Row label="Reporting Manager" value={e.reporting_manager} />
          </Section>

          <Section title="Salary & Bank" icon={CreditCard}>
            <Row label="Annual CTC" value={emp.salary?.annual_ctc ? `₹ ${Number(emp.salary.annual_ctc).toLocaleString("en-IN")}` : null} />
            <Row label="Bank" value={emp.salary?.bank_name} />
            <Row label="Account No" value={emp.salary?.account_number} />
            <Row label="IFSC" value={emp.salary?.ifsc} />
            <Row label="PAN" value={emp.salary?.pan} />
            <Row label="Aadhaar" value={emp.salary?.aadhaar} />
            <Row label="UAN" value={emp.salary?.uan} />
          </Section>

          <Section title={`Documents (${emp.documents?.length || 0})`} icon={FileText}>
            {(!emp.documents || emp.documents.length === 0) ? (
              <div className="text-sm text-slate-400">No documents uploaded.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {emp.documents.map((d, i) => (
                  <li key={`${d.name}-${d.file_name || i}`} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-700">{d.name}</div>
                      <div className="text-xs text-slate-500">{d.file_name}</div>
                    </div>
                    {d.file_url && (
                      <button
                        type="button"
                        onClick={() => downloadProtectedFile(d.file_url, d.file_name)}
                        className="text-[#226DB4] text-xs hover:underline"
                      >
                        Download
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={`Assigned Assets (${assets.length})`} icon={Laptop}>
            {assets.length === 0 ? (
              <div className="text-sm text-slate-400">No assets currently assigned.</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="employee-view-assets">
                {assets.map((a) => (
                  <li key={a.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-700">{a.name}</div>
                      <div className="text-xs text-slate-500">
                        {a.asset_type}
                        {a.serial_number && ` · ${a.serial_number}`}
                        {a.assigned_date && ` · since ${formatDisplayDate(a.assigned_date)}`}
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium bg-sky-100 text-sky-700 border-sky-200">
                      {a.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-[#f9fafb] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 rounded bg-white hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
